import { MediaImage } from "@/components/MediaImage";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export type FeatureRowItem = {
  image?: string;
  imageAlt?: string;
  title: string;
  body: string;
};

/**
 * Renders a list of feature/topic blocks in the SEO theme's
 * `service-entry-list-image` shape — image on one side, copy on the other,
 * alternating left/right.
 */
export function FeatureRow({
  items,
  imageFallback,
}: {
  items: FeatureRowItem[];
  /** Swapped in if an item's `image` fails to load (e.g. not yet generated). */
  imageFallback?: string;
}) {
  if (!items.length) return null;
  return (
    <section className="demo-feature-rows not-prose my-14 d-grid gap-12">
      {items.map((item, index) => {
        const reverse = index % 2 === 1;
        return (
          <article
            key={`${item.title}-${index}`}
            className={`demo-feature-row row g-4 g-lg-5 align-items-center ${reverse ? "flex-lg-row-reverse" : ""}`}
          >
            {item.image && (
              <div className="col-12 col-lg-6">
                <figure className="demo-feature-row-figure demo-image-frame demo-image-frame-wide m-0 overflow-hidden rounded-[10px] border border-border bg-card">
                  <MediaImage
                    src={item.image}
                    alt={item.imageAlt ?? item.title}
                    imgClassName="h-full w-full object-cover"
                    fallbackSrc={imageFallback}
                  />
                </figure>
              </div>
            )}
            <div className={item.image ? "col-12 col-lg-6" : "col-12"}>
              <div className="flex items-start gap-3">
                <h3 className="font-display text-2xl leading-tight">{item.title}</h3>
              </div>
              <div className="mt-4 text-base leading-relaxed text-muted-foreground [&_a]:text-primary [&_a:hover]:underline [&_p]:m-0">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.body}</ReactMarkdown>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}
